import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { CheckCircle2, XCircle, Clock, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface ValidacaoDocumento {
  id_usuario: string;
  nome_documento: string;
  retorno_documento: number;
  descricao_retorno?: string;
  created_at?: string;
}

interface DocumentosValidacaoProps {
  userId?: string;
}

export function DocumentosValidacao({ userId }: DocumentosValidacaoProps) {
  const [validacoes, setValidacoes] = useState<ValidacaoDocumento[]>([]);
  const [loading, setLoading] = useState(true);
  const [quantidadeEnviados, setQuantidadeEnviados] = useState(0);

  const carregarValidacoes = async () => {
    if (!userId) return;
    
    setLoading(true);
    try {
      // Buscar validações e quantidade de documentos enviados
      const [validacoesResult, evolucaoResult] = await Promise.all([
        supabase
          .from('validacao_documentos')
          .select('*')
          .eq('id_usuario', userId)
          .order('created_at', { ascending: false }),
        supabase
          .from('evolucao_contratacao')
          .select('quantidade_documentos_enviados')
          .eq('user_id', userId)
          .maybeSingle()
      ]);

      if (validacoesResult.error) {
        console.error('Erro ao carregar validações:', validacoesResult.error);
        return;
      }

      const validacoesData = validacoesResult.data || [];
      let quantidadeEnviados = evolucaoResult.data?.quantidade_documentos_enviados || 0;
      
      // 🔧 FALLBACK: Se quantidade_documentos_enviados está zerado mas existem validações,
      // usar o número real de validações como fallback
      if (quantidadeEnviados === 0 && validacoesData.length > 0) {
        const documentosComRetorno = validacoesData.filter(v => v.retorno_documento !== null && v.retorno_documento !== undefined).length;
        if (documentosComRetorno > 0) {
          console.warn('⚠️ FALLBACK: quantidade_documentos_enviados está zerado, usando contador de validações:', documentosComRetorno);
          quantidadeEnviados = documentosComRetorno;
        }
      }
      
      console.log('=== DEBUG CARREGAMENTO VALIDAÇÕES ===');
      console.log('Validações carregadas (raw):', validacoesData);
      console.log('Quantidade de documentos enviados (original):', evolucaoResult.data?.quantidade_documentos_enviados);
      console.log('Quantidade de documentos enviados (com fallback):', quantidadeEnviados);
      
      // Debug específico para Reservista
      const reservista = validacoesData.find(v => v.nome_documento === 'Reservista');
      if (reservista) {
        console.log('Documento Reservista encontrado:', reservista);
        console.log('Status retorno_documento:', reservista.retorno_documento);
        console.log('Tipo do retorno_documento:', typeof reservista.retorno_documento);
      }
      
      console.log('=== FIM DEBUG CARREGAMENTO ===');
      setValidacoes(validacoesData);
      
      // Verificar se todos os documentos enviados foram aprovados
      if (validacoesData && validacoesData.length > 0 && quantidadeEnviados > 0) {
        const documentosAprovados = validacoesData.filter(doc => doc.retorno_documento === 1).length;
        const documentosReprovados = validacoesData.filter(doc => doc.retorno_documento === 0).length;
        const todosAprovados = documentosAprovados === quantidadeEnviados;
        
        console.log('=== DEBUG DOCUMENTOS VALIDAÇÃO ===');
        console.log('Validações carregadas:', validacoesData);
        console.log('Quantidade de documentos enviados:', quantidadeEnviados);
        console.log(`Documentos aprovados: ${documentosAprovados}/${quantidadeEnviados}`);
        console.log('Documentos reprovados:', documentosReprovados);
        console.log('Todos os documentos enviados foram aprovados:', todosAprovados);
        console.log('Pode avançar de etapa:', todosAprovados && documentosReprovados === 0);
        console.log('=== FIM DEBUG ===');
        
        if (todosAprovados && documentosReprovados === 0) {
          // Disparar evento customizado para notificar que todos foram aprovados
          window.dispatchEvent(new CustomEvent('documentosAprovados', { 
            detail: { 
              userId, 
              todosAprovados: true,
              quantidadeEnviados,
              quantidadeAprovados: documentosAprovados
            }
          }));
        }
      }
    } catch (error) {
      console.error('Erro ao carregar validações:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    carregarValidacoes();
  }, [userId]);

  // Buscar quantidade de documentos enviados da evolucao_contratacao
  useEffect(() => {
    const buscarQuantidadeEnviados = async () => {
      if (!userId) return;
      
      const { data } = await supabase
        .from('evolucao_contratacao')
        .select('quantidade_documentos_enviados')
        .eq('user_id', userId)
        .maybeSingle();
      
      if (data?.quantidade_documentos_enviados) {
        setQuantidadeEnviados(data.quantidade_documentos_enviados);
      }
    };
    
    buscarQuantidadeEnviados();
  }, [userId]);

  if (loading) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center justify-center space-x-2">
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary"></div>
            <span>Carregando status dos documentos...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (validacoes.length === 0) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="text-center text-muted-foreground">
            <Clock className="w-8 h-8 mx-auto mb-2" />
            <p>Aguardando validação dos documentos...</p>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={carregarValidacoes}
              className="mt-2"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Atualizar
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Debug da categorização dos documentos
  console.log('=== DEBUG CATEGORIZAÇÃO DOCUMENTOS ===');
  console.log('Todas as validações:', validacoes);
  validacoes.forEach(v => {
    console.log(`Documento: ${v.nome_documento}, retorno: ${v.retorno_documento}, tipo: ${typeof v.retorno_documento}`);
  });
  
  const documentosAprovados = validacoes.filter(v => v.retorno_documento === 1);
  const documentosReprovados = validacoes.filter(v => v.retorno_documento === 0);
  const documentosPendentes = validacoes.filter(v => v.retorno_documento === null || v.retorno_documento === undefined);
  
  console.log('Documentos aprovados:', documentosAprovados);
  console.log('Documentos reprovados:', documentosReprovados);
  console.log('Documentos pendentes:', documentosPendentes);
  console.log('=== FIM DEBUG CATEGORIZAÇÃO ===');
  
  // Lógica de validação corrigida
  const totalDocumentosProcessados = documentosAprovados.length + documentosReprovados.length;
  const validacaoCompleta = quantidadeEnviados > 0 && totalDocumentosProcessados === quantidadeEnviados;
  const todosAprovados = validacaoCompleta && documentosAprovados.length === quantidadeEnviados;
  const validacaoEmAndamento = quantidadeEnviados > 0 && totalDocumentosProcessados < quantidadeEnviados;
  const todosReprovados = validacaoCompleta && documentosReprovados.length === quantidadeEnviados;

  // Função para formatar nome do documento
  const formatarNomeDocumento = (nomeDocumento: string) => {
    const mapeamentos: { [key: string]: string } = {
      'rg_frente': 'RG (Frente)',
      'rg_verso': 'RG (Verso)',
      'carteira_trabalho': 'Carteira de Trabalho',
      'certificado_reservista': 'Certificado de Reservista',
      'Reservista': 'Certificado de Reservista',
      'RG': 'RG',
      'Carteira de trabalho': 'Carteira de Trabalho',
      'Dados bancarios': 'Comprovante Bancário',
      'foto_3x4': 'Foto 3x4',
      'Foto 3x4': 'Foto 3x4',
      'comprovante_bancario': 'Comprovante Bancário',
      'comprovante_residencia': 'Comprovante de Residência',
      'Comprovante de residencia': 'Comprovante de Residência',
      'comprovante_escolaridade': 'Comprovante de Escolaridade',
      'Comprovante de escolaridade': 'Comprovante de Escolaridade',
      'titulo_eleitor': 'Título de Eleitor',
      'Titulo de eleitor': 'Título de Eleitor',
      'certidao_nascimento': 'Certidão de Nascimento',
      'Certidão de nascimento': 'Certidão de Nascimento',
      'certidao_casamento': 'Certidão de Casamento',
      'atestado_saude': 'Atestado de Saúde'
    };

    console.log('=== DEBUG FORMATAÇÃO DOCUMENTO ===');
    console.log('Nome documento original:', nomeDocumento);
    console.log('Nome documento mapeado:', mapeamentos[nomeDocumento] || nomeDocumento.replace(/_/g, ' '));
    console.log('=== FIM DEBUG FORMATAÇÃO ===');

    // Para comprovantes de vacinação com formato "CV dependente X"
    if (nomeDocumento.startsWith('CV dependente')) {
      const match = nomeDocumento.match(/CV dependente (\d+)/);
      if (match) {
        const numeroDependente = match[1];
        return `Dependente ${numeroDependente} - Comprovante de Vacinação`;
      }
    }

    // Para dependentes com formato "dependente_X_documento"
    if (nomeDocumento.startsWith('dependente_')) {
      const match = nomeDocumento.match(/dependente_(\d+)_(.+)/);
      if (match) {
        const numeroDependente = match[1];
        const tipoDoc = match[2];
        
        const docMapeado = mapeamentos[tipoDoc] || tipoDoc.replace(/_/g, ' ');
        
        if (tipoDoc === 'certidao_nascimento') {
          return `Dependente ${numeroDependente} - Certidão de Nascimento`;
        } else if (tipoDoc === 'comprovante_escolaridade') {
          return `Dependente ${numeroDependente} - Comprovante de Escolaridade`;
        } else if (tipoDoc === 'comprovante_vacinacao') {
          return `Dependente ${numeroDependente} - Comprovante de Vacinação`;
        } else if (tipoDoc.includes('rg_')) {
          const parte = tipoDoc === 'rg_frente' ? 'Frente' : 'Verso';
          return `Dependente ${numeroDependente} - RG (${parte})`;
        }
        
        return `Dependente ${numeroDependente} - ${docMapeado}`;
      }
    }

    return mapeamentos[nomeDocumento] || nomeDocumento.replace(/_/g, ' ');
  };

  return (
    <div className="space-y-4">
      {/* Validação em Andamento */}
      {validacaoEmAndamento && (
        <Card className="border-blue-200 bg-blue-50">
          <CardContent className="pt-6">
            <div className="flex items-center justify-center space-x-2 text-blue-800">
              <Clock className="h-6 w-6 text-blue-600 animate-pulse" />
              <div className="text-center">
                <h4 className="font-semibold">Validação em Andamento</h4>
                <p className="text-sm mt-1">
                  Seus documentos estão sendo validados. Este processo pode durar até 10 minutos.
                </p>
                <p className="text-xs mt-2 text-blue-600">
                  Processados: {totalDocumentosProcessados}/{quantidadeEnviados} documentos
                </p>
                <p className="text-xs mt-1 text-blue-600">
                  Você pode sair da página e voltar outro momento.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Status da Validação Concluída com Sucesso */}
      {todosAprovados && (
        <Card className="border-green-200 bg-green-50">
          <CardContent className="pt-6">
            <div className="flex items-center justify-center space-x-2 text-green-800">
              <CheckCircle2 className="h-6 w-6 text-green-600" />
              <div className="text-center">
                <h4 className="font-semibold">Validação Concluída com Sucesso!</h4>
                <p className="text-sm mt-1">Todos os seus documentos foram aprovados. Você será redirecionado automaticamente.</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Status Reprovado */}
      {todosReprovados && (
        <Card className="border-red-200 bg-red-50">
          <CardContent className="pt-6">
            <div className="flex items-center justify-center space-x-2 text-red-800">
              <XCircle className="h-6 w-6 text-red-600" />
              <div className="text-center">
                <h4 className="font-semibold">Documentação Reprovada</h4>
                <p className="text-sm mt-1">Alguns documentos foram reprovados. Corrija e reenvie os documentos indicados abaixo.</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Documentos Pendentes */}
      {documentosPendentes.length > 0 && !todosAprovados && !todosReprovados && (
        <Card className="border-yellow-200 bg-yellow-50">
          <CardHeader className="pb-3">
            <CardTitle className="text-yellow-800 text-sm sm:text-base">Documentos em Validação</CardTitle>
            <CardDescription className="text-yellow-600 text-xs sm:text-sm">
              Os seguintes documentos estão sendo validados:
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            {documentosPendentes.map((validacao) => (
              <div 
                key={`${validacao.nome_documento}-${validacao.created_at}`} 
                className="flex items-center justify-between p-2 bg-white rounded border"
              >
                <span className="font-medium text-yellow-800 text-xs sm:text-sm break-words flex-1 mr-2">
                  {formatarNomeDocumento(validacao.nome_documento)}
                </span>
                <Clock className="h-4 w-4 sm:h-5 sm:w-5 text-yellow-500 flex-shrink-0" />
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Documentos Aprovados */}
      {documentosAprovados.length > 0 && (
        <Card className="border-green-200 bg-green-50">
          <CardHeader className="pb-3">
            <CardTitle className="text-green-800 text-sm sm:text-base">Documentos Aprovados</CardTitle>
            <CardDescription className="text-green-600 text-xs sm:text-sm">
              Os seguintes documentos foram validados com sucesso:
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            {documentosAprovados.map((validacao) => (
              <div 
                key={`${validacao.nome_documento}-${validacao.created_at}`} 
                className="flex items-center justify-between p-2 bg-white rounded border"
              >
                <span className="font-medium text-green-800 text-xs sm:text-sm break-words flex-1 mr-2">
                  {formatarNomeDocumento(validacao.nome_documento)}
                </span>
                <CheckCircle2 className="h-4 w-4 sm:h-5 sm:w-5 text-green-500 flex-shrink-0" />
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Documentos Reprovados */}
      {documentosReprovados.length > 0 && (
        <Card className="border-red-200 bg-red-50">
          <CardHeader className="pb-3">
            <CardTitle className="text-red-800 text-sm sm:text-base break-words">
              Documentos que Precisam ser Corrigidos
            </CardTitle>
            <CardDescription className="text-red-600 text-xs sm:text-sm break-words">
              Os seguintes documentos foram reprovados e precisam ser reenviados:
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            {documentosReprovados.map((validacao) => (
              <div 
                key={`${validacao.nome_documento}-${validacao.created_at}`} 
                className="p-2 bg-white rounded border border-red-200"
              >
                <div className="flex items-center justify-between">
                  <span className="font-medium text-red-800 text-xs sm:text-sm break-words flex-1 mr-2">
                    {formatarNomeDocumento(validacao.nome_documento)}
                  </span>
                  <XCircle className="h-4 w-4 sm:h-5 sm:w-5 text-red-500 flex-shrink-0" />
                </div>
                {validacao.descricao_retorno && (
                  <p className="text-xs sm:text-sm text-red-600 mt-1 bg-red-50 p-2 rounded border break-words">
                    <strong>Motivo:</strong> {validacao.descricao_retorno}
                  </p>
                )}
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      <div className="flex justify-center">
        <Button 
          variant="outline" 
          size="sm" 
          onClick={carregarValidacoes}
          className="w-full sm:w-auto"
        >
          <RefreshCw className="w-4 h-4 sm:mr-2" />
          <span className="hidden sm:inline ml-2">Atualizar Status</span>
          <span className="sm:hidden">Atualizar</span>
        </Button>
      </div>
    </div>
  );
}